import React from 'react';
import {
    Box,
    Typography,
    Grid,
    Divider
} from '@mui/material';
import { TourOverview } from '../../../services/restserver-openapi';
import TourCardPublic from "./TourCardPublic";
import TourGridSkeleton from "./TourGridSkeleton";
import { useAppSelector } from "../../../hooks/hooks";

interface TourGridProps {
    showOwner: boolean;
    loading: boolean;
    tours: TourOverview[] | null | undefined;
    grouped?: boolean;
}

const getFirstStartDate = (tour: TourOverview): number | undefined =>
    tour.stays?.length ? Math.min(...tour.stays.map(s => s.fromDate ?? Infinity)) : undefined;

const getLastEndDate = (tour: TourOverview): number | undefined =>
    tour.stays?.length ? Math.max(...tour.stays.map(s => s.toDate ?? 0)) : undefined;

export const categorizeTours = (tours: TourOverview[] | null | undefined): {
    future?: TourOverview[];
    ongoing?: TourOverview[];
    past?: TourOverview[];
    canceled?: TourOverview[];
} => {
    if (!tours) {
        return { future: undefined, ongoing: undefined, past: undefined, canceled: undefined };
    }

    const now = Date.now();
    const future: TourOverview[] = [];
    const ongoing: TourOverview[] = [];
    const past: TourOverview[] = [];
    const canceled: TourOverview[] = [];

    for (const tour of tours) {
        if (tour.status === 'CANCELED') {
            canceled.push(tour);
            continue;
        }
        if (tour.tourCompleted) {
            past.push(tour);
            continue;
        }
        const endDate = getLastEndDate(tour);
        if (typeof endDate === 'number' && endDate > 0 && endDate < now) {
            past.push(tour);
            continue;
        }
        const startDate = getFirstStartDate(tour);
        if (startDate !== undefined) {
            if (startDate > now) {
                future.push(tour);
            } else {
                ongoing.push(tour);
            }
        } else {
            future.push(tour);
        }
    }

    return { future, ongoing, past, canceled };
};

const renderTourCards = (
    tours: TourOverview[],
    showOwner: boolean,
    currentUserCommunity: string | undefined
) => (
    <Grid container spacing={3}>
        {tours.map((tour) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={tour.tourId}>
                <TourCardPublic
                    tour={tour}
                    showOwner={showOwner}
                    isOwner={tour.owner === currentUserCommunity}
                />
            </Grid>
        ))}
    </Grid>
);

const Section = ({
                     title,
                     tours,
                     showOwner,
                     textIfEmpty,
                     loading = false,
                     currentUserCommunity
                 }: {
    title: string;
    tours?: TourOverview[];
    showOwner: boolean;
    textIfEmpty: string;
    loading?: boolean;
    currentUserCommunity: string | undefined;
}) => (
    <Box mb={4}>
        <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={700}
            sx={{ textTransform: 'uppercase', mb: 2, display: 'block' }}
        >
            {title}
        </Typography>

        {loading || tours === null
            ? <TourGridSkeleton count={3} />
            : tours && tours.length > 0
                ? renderTourCards(tours, showOwner, currentUserCommunity)
                : <Typography>{textIfEmpty}</Typography>}
    </Box>
);

export default function TourGrid({ loading, tours, grouped = true, showOwner }: TourGridProps) {
    const currentUserCommunity = useAppSelector((state) => state.user.user?.communityName);

    if (tours === undefined) {
        return <Typography color="error">Fehler beim Laden der Reisen.</Typography>;
    }

    if (!grouped) {
        if (loading || tours === null) return <TourGridSkeleton count={3} />;
        if (!tours || tours.length === 0) return <Typography variant="body2" color="text.secondary">Keine Reisen gefunden.</Typography>;
        return renderTourCards(tours, showOwner, currentUserCommunity);
    }

    const { future, ongoing, past } = categorizeTours(tours);

    return (
        <Box>
            <Section
                title="Bevorstehende Reisen:"
                tours={future}
                showOwner={showOwner}
                textIfEmpty="Keine Reisen geplant."
                loading={loading}
                currentUserCommunity={currentUserCommunity}
            />
            <Divider sx={{ my: 2 }} />
            <Section
                title="Aktuelle Reisen:"
                tours={ongoing}
                showOwner={showOwner}
                textIfEmpty="Derzeit finden keine Reisen statt."
                loading={loading}
                currentUserCommunity={currentUserCommunity}
            />
            <Divider sx={{ my: 2 }} />
            <Section
                title="Vergangene Reisen:"
                tours={past}
                showOwner={showOwner}
                textIfEmpty="Keine Reisen durchgeführt."
                loading={loading}
                currentUserCommunity={currentUserCommunity}
            />
        </Box>
    );
}
